<template src='./betterInputTag.pug' lang="pug"></template>

<script>
import { debounce as _debounce } from '../../../utils/helpers'
import KeyCodeEnum from '../../../enum/keyCode'

export default {
  name: 'BetterInputTag',

  props: {
    defaultTagsIfEmpty: {
      type: Array,
      default: () => []
    },
    typeable: {
      type: Boolean,
      default: true
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    tags: {
      type: Array,
      default: () => []
    },
    props: {
      type: Object,
      default: () => ({
        id: 'id',
        name: 'name'
      })
    },
    typeOnly: {
      type: Boolean,
      default: false
    },
    typeOnlyOptions: {
      type: Object,
      default: () => ({})
    },
    fnGetDataFromServer: {
      type: Function,
      default: async () => Promise.resolve([])
    },
    possibleTagsSearchTexts: {
      type: Object,
      default: () => ({
        noResult: 'geral.labels.semResultadosSingular',
        empty: 'geral.labels.listaVazia'
      })
    },
    placeHolders: {
      type: Object,
      default: () => ({
        input: 'estabelecimentos.labels.filtro.buscar'
      })
    },
    disableInput: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      eventTypes: {
        SEARCH: 0,
        TYPE_TO_START_SEARCHING: 1,
        NOOP: 2,
        SHOW_EMPTY_LIST: 3
      },
      currentMaxWidth: 80,
      tag: '',
      input: null,
      inputExists: this.typeable && !this.readOnly,
      paddingClass: this.readOnly || !this.disableInput ? 'full-padding' : 'left-padding',
      showingPossibilities: false,
      highlightedIndex: 0,
      possibilities: [],
      selectedEveryThing: false,
      debounce: _debounce.apply(this)
    }
  },

  computed: {
    expandedMenu () {
      return this.$store.getters.expandedMenu
    }
  },

  watch: {
    tags: {
      handler () {
        this.resetInputWidth()
      },
      deep: true
    },
    expandedMenu () {
      this.resetInputWidth(200)
    }
  },

  methods: {
    boxClicked ({ target }) {
      if (!this.inputExists) {
        return
      }

      const inputs = target.getElementsByTagName('input')
      let input = null

      if (inputs !== null) {
        input = inputs[0]
      }

      if (this.input === null && input !== null) {
        this.input = input
      }

      this.input.focus()
    },

    handleKey (evt) {
      if (!this.inputExists) {
        return
      }

      if (this.typeOnly) {
        this.handleTypeOnly(evt)
        return
      }

      const nextEvent = this.handleInteraction(evt)

      if (nextEvent === this.eventTypes.SEARCH) {
        this.handleDataFromServer()
      } else if (nextEvent === this.eventTypes.TYPE_TO_START_SEARCHING) {
        this.possibilities = this.getStartToType()
      } else if (nextEvent === this.eventTypes.SHOW_EMPTY_LIST) {
        this.possibilities = this.getEmptyList()
      }
    },

    handleTypeOnly (evt) {
      const tag = {
        [this.props.name]: this.tag,
        [this.props.id]: this.tag
      }

      if (this.tag) {
        let toAdd = false
        if ([KeyCodeEnum.TAB, KeyCodeEnum.ENTER, KeyCodeEnum.BACKSPACE].includes(evt.keyCode)) {
          toAdd = true
        } else if (this.typeOnlyOptions.isNumericOnly && evt.keyCode === KeyCodeEnum.SPACE_BAR) {
          toAdd = true
        }

        if (toAdd) {
          evt.preventDefault()
          this.addTag(tag)
          this.input.focus()
        }
      } else if (!this.tag && evt.keyCode === KeyCodeEnum.BACKSPACE) {
        this.removeTag(this.tags.length - 1)
      }
    },

    getStartToType () {
      return [{
        [this.props.name]: this.$t('geral.labels.digiteParaPesquisar'),
        __blocked: true
      }]
    },

    handleDataFromServer () {
      if (!this.tag) {
        this.possibilities = this.getStartToType()
        return
      }

      this.possibilities = [{
        [this.props.name]: this.$t('geral.labels.carregando'),
        __blocked: true
      }]

      this.showPossibilities()

      this.debounce(this.getDataFromServer, 250)
    },

    getDataFromServer () {
      if (this.typeOnly) {
        return
      }
      this.fnGetDataFromServer(this.tag, this.tags)
        .then(response => {
          if (response.length === 0) {
            this.possibilities = this.getEmptyList()
          } else {
            this.highlightedIndex = 0
            this.possibilities = response
          }
          this.updateListOfPossibilitiesMargin()
        })
        .catch(e => {
          if (e.message !== 'user.cancelled') {
            this.$root.$refs.notification.error(e.message)
          }
        })
    },

    handleInteraction (evt) {
      if (evt.keyCode === KeyCodeEnum.BACKSPACE) {
        if (!this.tag) {
          this.removeTag(this.tags.length - 1)
          return this.eventTypes.TYPE_TO_START_SEARCHING
        }

        if (this.selectedEveryThing) {
          this.selectedEveryThing = false
          return this.eventTypes.TYPE_TO_START_SEARCHING
        }

        evt.preventDefault()

        if (evt.ctrlKey) {
          this.tag = ''
          return this.eventTypes.TYPE_TO_START_SEARCHING
        }

        this.tag = this.tag.substring(0, this.tag.length - 1)
        return this.eventTypes.SEARCH
      }

      if (evt.ctrlKey) {
        if (evt.keyCode === KeyCodeEnum.A) {
          this.selectedEveryThing = true
        }
        return this.eventTypes.NOOP
      }

      evt.preventDefault()

      if (evt.key.length === 1) {
        if (this.selectedEveryThing) {
          this.tag = evt.key
        } else {
          this.tag += evt.key
        }
        this.selectedEveryThing = false
        return this.eventTypes.SEARCH
      }

      this.selectedEveryThing = false

      if (evt.keyCode === KeyCodeEnum.ENTER) {
        this.addTag(this.possibilities[this.highlightedIndex])
        return this.eventTypes.TYPE_TO_START_SEARCHING
      }

      if ([KeyCodeEnum.ARROW_UP, KeyCodeEnum.ARROW_DOWN].includes(evt.keyCode)) {
        this.highlightedIndex += evt.keyCode === KeyCodeEnum.ARROW_UP ? -1 : 1

        if (this.highlightedIndex < 0) {
          this.highlightedIndex = 0
        } else if (this.possibilities.length === 1 && this.possibilities[0].__blocked) {
          this.highlightedIndex = -1
        } else if (this.highlightedIndex >= this.possibilities.length) {
          this.highlightedIndex = this.possibilities.length - 1
        }
        return this.eventTypes.NOOP
      }
      return this.eventTypes.SEARCH
    },

    handleMouse (tag, index) {
      if (!tag.__blocked) {
        this.highlightedIndex = index
      }
    },

    addTag (tag) {
      if (!tag) {
        return
      }

      if (tag.__blocked) {
        this.possibilities = this.getStartToType()
        return
      }

      this.showingPossibilities = false

      this.input.blur()

      let index = -1
      if (this.typeOnly) {
        index = this.tags.findIndex(added => added[this.props.id] === tag[this.props.id])
      } else {
        index = this.tags.lastIndexOf(tag)
      }

      if (index === -1) {
        if (!this.validateTypeOnly(tag[this.props.name])) {
          return
        }
        this.possibilities = []
        this.tags.push(tag)
        this.highlightedIndex = -1
      }
      this.$emit('update:tags', this.tags)
    },

    removeTag (index) {
      this.tags.splice(index, 1)
      this.$emit('update:tags', this.tags)
      this.tag = ''
      this.possibilities = []
    },

    validateTypeOnly (value) {
      if (this.typeOnlyOptions.isNumericOnly) {
        return /^\d+$/.test(value)
      }
      return true
    },

    resetInputWidth (delay = 0) {
      if (!this.inputExists) {
        return
      }
      this.input.style.width = '55px'
      this.$nextTick(() => {
        setTimeout(() => {
          const width = (this.input.parentElement.clientWidth - this.input.offsetLeft - 10) + 'px'
          this.input.style.width = width
          this.updateListOfPossibilitiesMargin()
        }, delay)
      })
    },

    getEmptyList () {
      const possibility = {
        __blocked: true
      }

      let name
      if (this.tag) {
        name = this.$t(this.possibleTagsSearchTexts.noResult)
      } else {
        name = this.$t(this.possibleTagsSearchTexts.empty)
      }

      possibility[this.props.name] = name
      return [possibility]
    },

    showPossibilities () {
      if (this.typeOnly) {
        return
      }
      this.showingPossibilities = true
      this.$nextTick(() => {
        if (this.possibilities.length === 0 && this.tag === '') {
          this.possibilities = this.getStartToType()
        }
        this.updateListOfPossibilitiesMargin()
      })
    },

    updateListOfPossibilitiesMargin () {
      const divListOfPossibilities = this.getDivListOfPossibilities()

      if (divListOfPossibilities) {
        const height = divListOfPossibilities.parentElement.clientHeight
        divListOfPossibilities.style.marginTop = height + 'px'
      }
    },

    getDivListOfPossibilities () {
      const listDivsListOfPossibilities = this.$el.getElementsByClassName('possibilities-list')
      let divListOfPossibilities
      if (listDivsListOfPossibilities) {
        divListOfPossibilities = listDivsListOfPossibilities[0]
      }
      return divListOfPossibilities
    },

    close () {
      if (this.typeOnly) {
        if (this.tag) {
          this.addTag({
            [this.props.name]: this.tag,
            [this.props.id]: this.tag
          })
        }
      } else {
        this.showingPossibilities = false
        this.possibilities = this.getStartToType()
      }
      this.tag = ''
    }
  },

  mounted () {
    if (!this.inputExists) {
      return
    }

    this.input = new XPathEvaluator()
                    .createExpression('div/input')
                    .evaluate(this.$el, XPathResult.FIRST_ORDERED_NODE_TYPE)
                    .singleNodeValue

    window.addEventListener('resize', () => {
      this.resetInputWidth(200)
    })
  }
}
</script>

<style scoped>
.better-input-tag {
  background: white;
  min-height: 36px;
  border: 1px solid #ccc;
  display: flex;
  align-items: left;
  flex-wrap: wrap;
  justify-content: flex-start;
}

.better-input-tag.possibilities-list-closed {
  border-top-left-radius: 5px;
  border-bottom-left-radius: 5px;
}

.better-input-tag.possibilities-list-open {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.full-radius {
  border-radius: 5px;
}

.better-input-tag > input {
  outline: 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  margin: 5px;
  min-width: 55px;
}

.better-input-tag > span {
  border-radius: 2px;
  color: #1c3b60;
  border: 1px solid #ccc;
  background-color: #f3f8fe;
  text-align: center;
  margin: 5px 0 5px 5px;
  line-height: 20px;
}

.better-input-tag > span > a {
  text-decoration: none;
  color: #1c3b60;
  font-size: 10px;
  font-weight: bold;
  padding: 7px;
  height: 10px;
  vertical-align: top;
}

.better-input-tag > .full-padding {
  padding: 0 5px 0 5px;
}

.better-input-tag > .left-padding {
  padding-left: 5px;
}

.possibilities-list {
  margin-left: -1px;
  cursor: pointer;
  position: absolute;
  background-color: #ffffff;
  z-index: 900;
  border-bottom-left-radius: 3px;
  border-bottom-right-radius: 3px;
  border: 1px solid #ccc;
  max-height: 300px;
  overflow: auto;
}

.possibilities-list > .possibilities-list-item {
  padding: 10px 10px;
  color: #444;
}

.possibilities-list > .possibilities-list-item.highlighted:not(.blocked) {
  color: #000;
  background-color: #eee;
}

.disable-input {
  background-color: #f8f8f8;
  cursor: default;
}
</style>



// WEBPACK FOOTER //
// BetterInputTag.vue?390d96dc